import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Typography,
  Box,
  Grid,
  Paper,
  CircularProgress,
  TextField,
  IconButton,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  MenuItem,
  Select,
  Alert as MuiAlert,
  InputAdornment,
  Snackbar,
  Checkbox
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from '../../../contexts/AuthContext';
import { getCashboxesMap, getBankAccountsMap } from '../../../services/migration/financialDataService';
import { createPersonnelTransaction } from '../../../services/migration/personnelTransactionService';
import ProjectSelector from '../../accounting/shared/ProjectSelector';
import { AppDatePicker } from '../../common/AppDatePicker';
import GuideButton from '../../common/guide/GuideButton';

// Ödeme yöntemleri (şimdilik kalabilir, sonra personel için özelleşebilir)
const paymentMethods = [
  { id: 'accrual', name: 'Tahakkuk' },
  { id: 'cash', name: 'Nakit' }, 
  { id: 'bank', name: 'Banka' }, // Örnek, personel için farklı olabilir
];

interface Currency {
  id: string;
  name: string;
  symbol: string;
  code: string;
  isDefault?: boolean;
}

// Cashbox arayüzü MakePaymentModal.tsx'den referans alınarak güncellendi
interface Cashbox {
  id: string;
  name: string;
  description?: string;
  currency: string; 
  balance: number;
  createdAt?: string;
  isActive?: boolean;
  branchId?: string | null;
  isDefault?: boolean;
  hasTransactions?: boolean;
  currencyCode?: string; 
  currencySymbol?: string;
}

// BankAccount arayüzü MakePaymentModal.tsx'den referans alınarak güncellendi
interface BankAccount {
  id: string;
  bankName: string;
  accountName?: string;
  branchName?: string;
  accountNumber: string;
  iban?: string;
  currency: string; 
  balance: number;
  isActive?: boolean;
  isPosAccount?: boolean; 
  isCheckAccount?: boolean;
  currencySymbol?: string;
  isSubAccount?: boolean;
  parentBankAccountId?: string;
}

// Veri kaydetme için arayüz ekleyelim
interface PersonnelTransaction {
  id?: string | null; // Firebase'den push sonrası atanıyor, null olabilir
  personnelId: string;
  personnelName: string;
  companyId: string | null;
  amount: number;
  description: string;
  paymentMethod: string;
  currency?: string; // Undefined olabilir
  transactionType: string;
  cashboxId: string | null | undefined;
  cashboxName: string | null | undefined;
  bankAccountId: string | null | undefined;
  bankAccountName: string | null | undefined;
  performedBy: string;
  createdAt: string;
  isAccrual: boolean;
}

interface AddDebtModalProps {
  open: boolean;
  onClose: () => void;
  personnelId: string;
  personnelName: string;
  companyId: string | null;
  sourceAction?: 'debt_receivable' | 'payment_collection';
  onSuccess?: () => void;
  initialPaymentDescription?: string;
  initialPaymentAmount?: string;
  /** ADVANCE: bu ödeme bir avanstır (maaş oluştururken avans uyarısı için işaretlenir). */
  transactionKind?: 'ADVANCE';
  /** Şube yöneticisi: yalnız bu şubenin kasa/banka hesapları listelenir. */
  restrictToBranchId?: string;
  hideProject?: boolean;
  /**
   * Maaş/prim hakedişi ödemesi: ödeme bu hakedişe bağlanır (AccrualId). Para birimi hakedişinkine
   * kilitlenir, Tahakkuk seçilemez (gerçek para çıkışı), tutar kalan tutarı aşamaz.
   */
  accrualId?: string;
  accrualCurrency?: string;
  accrualRemaining?: number;
}

const CURRENCY_NAMES: Record<string, string> = { TRY: 'Türk Lirası', USD: 'Amerikan Doları', EUR: 'Euro', GBP: 'İngiliz Sterlini' };

const AddDebtModal: React.FC<AddDebtModalProps> = ({
  open,
  onClose,
  personnelId,
  personnelName,
  companyId,
  sourceAction,
  onSuccess,
  initialPaymentDescription,
  initialPaymentAmount,
  transactionKind,
  restrictToBranchId,
  hideProject = false,
  accrualId,
  accrualCurrency,
  accrualRemaining
}) => {
  const isAccrualPayment = !!accrualId;
  const lockedCurrencyCode = (accrualCurrency || 'TRY').toUpperCase();
  const availablePaymentMethods = isAccrualPayment ? paymentMethods.filter(m => m.id !== 'accrual') : paymentMethods;
  const inBranch = (branchId?: string | null) =>
    restrictToBranchId === undefined || (!!branchId && branchId === restrictToBranchId);
  const { userDetails } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [insufficientFunds, setInsufficientFunds] = useState(false);
  const [insufficientFundsMessage, setInsufficientFundsMessage] = useState('');
  
  const [selectedCurrency, setSelectedCurrency] = useState<Currency | null>(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('cash');
  const [selectedCashbox, setSelectedCashbox] = useState<Cashbox | null>(null);
  const [selectedBankAccount, setSelectedBankAccount] = useState<BankAccount | null>(null);
  const [amount, setAmount] = useState<string>('');
  const [description, setDescription] = useState('');
  // PYS: hareketi bir projeye bağlar (opsiyonel)
  const [projectId, setProjectId] = useState<number | null>(null);

  // ✅ Geçmiş tarihli işlem: toggle açıkken seçilen tarihe TAMAMEN yazılır (Date + CreatedAt).
  const [isBackdated, setIsBackdated] = useState(false);
  const [transactionDate, setTransactionDate] = useState<string>(() => {
    const t = new Date();
    return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
  });

  const [cashboxes, setCashboxes] = useState<Cashbox[]>([]); 
  const [filteredCashboxes, setFilteredCashboxes] = useState<Cashbox[]>([]);
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]); 
  const [filteredBankAccounts, setFilteredBankAccounts] = useState<BankAccount[]>([]);

  // Snackbar state'leri
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  const getUserFullName = (): string => {
    if (userDetails && userDetails.firstName && userDetails.lastName) {
      return `${userDetails.firstName} ${userDetails.lastName}`;
    } else if (userDetails && userDetails.firstName) {
      return userDetails.firstName;
    } else if (userDetails && userDetails.email) {
      return userDetails.email;
    } else {
      return 'Sistem';
    }
  };

  const getCurrencySymbol = (code: string): string => {
    switch(code) {
      case 'TRY': return '₺';
      case 'USD': return '$';
      case 'EUR': return '€';
      case 'GBP': return '£';
      default: return code;
    }
  };

  // İşlem tarihi string'inden (YYYY-MM-DD) Date üret (öğlen sabitli; gün kaymasını önler)
  const parseTransactionDate = (value: string): Date | null => {
    const parts = value.split('-');
    if (parts.length !== 3) return null;
    const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]), 12, 0, 0);
    return isNaN(d.getTime()) ? null : d;
  };

  const formatCurrency = (num: number, currencyCode: string = 'TRY') => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);
  };

  const loadCashboxesAndBanks = async () => {
    if (!companyId || !selectedCurrency) return;
    setLoading(true);
    setSubmitError(null);

    try {
      // ✅ SQL: kasaları yükle (gts_accounting_api /api/Cashbox)
      const cashboxData = await getCashboxesMap(companyId);
      const cashboxList: Cashbox[] = [];
      if (cashboxData) {
        Object.keys(cashboxData).forEach(id => {
          const data = cashboxData[id];
          if (!inBranch(data.branchId)) return;
          const currencyCode = data.currency || 'TRY';
          cashboxList.push({
            id,
            name: data.name || '',
            description: data.description || '',
            currency: currencyCode,
            currencyCode: currencyCode,
            currencySymbol: getCurrencySymbol(currencyCode),
            balance: data.balance || 0,
            createdAt: data.createdAt || '',
            isActive: data.isActive !== false,
            branchId: data.branchId || null,
            isDefault: data.isDefault || false,
            hasTransactions: data.hasTransactions || false
          });
        });
        cashboxList.sort((a, b) => {
          if (a.name === "Genel Kasa") return -1;
          if (b.name === "Genel Kasa") return 1;
          return a.name.localeCompare(b.name);
        });
      }
      setCashboxes(cashboxList);

      // ✅ SQL: banka hesaplarını yükle (gts_accounting_api /api/BankAccount)
      const accountsData = await getBankAccountsMap(companyId);
      const bankAccountList: BankAccount[] = [];
      if (accountsData) {
        Object.keys(accountsData).forEach(id => {
          const data = accountsData[id];
          if (!inBranch(data.branchId)) return;
          const currencyCode = data.currency || 'TRY';
          bankAccountList.push({
            id,
            bankName: data.bankName || '',
            accountName: data.accountName || '',
            branchName: data.branchName || '',
            accountNumber: data.accountNumber || '',
            iban: data.iban || '',
            currency: currencyCode,
            currencySymbol: getCurrencySymbol(currencyCode),
            balance: data.balance || 0,
            isActive: data.isActive !== false,
            isPosAccount: data.isPosAccount || false,
            isCheckAccount: data.isCheckAccount || false,
            isSubAccount: false
          });
          const hesaplar = data.hesaplar;
          if (hesaplar) {
            Object.keys(hesaplar).forEach(subId => {
              const subAccount = hesaplar[subId];
              const subCurrencyCode = subAccount.currency || 'TRY';
              bankAccountList.push({
                id: subId,
                bankName: `${data.bankName} - ${subAccount.accountName || 'Alt Hesap'}`,
                accountName: subAccount.accountName || 'Alt Hesap',
                branchName: data.branchName || '',
                accountNumber: subAccount.accountNumber || '',
                iban: subAccount.iban || '',
                currency: subCurrencyCode,
                currencySymbol: getCurrencySymbol(subCurrencyCode),
                balance: subAccount.balance || 0,
                isActive: subAccount.isActive !== false,
                isPosAccount: subAccount.isPosAccount || false,
                isCheckAccount: subAccount.isCheckAccount || false,
                isSubAccount: true,
                parentBankAccountId: id
              });
            });
          }
        });
        bankAccountList.sort((a, b) => a.bankName.localeCompare(b.bankName));
      }
      setBankAccounts(bankAccountList);

    } catch (err: any) {
      console.error("Kasa/Banka verileri yüklenirken hata: ", err);
      setSubmitError("Kasa ve banka bilgileri yüklenemedi: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      // Hakediş ödemesinde para birimi hakedişinkidir; yalnız o para birimindeki kasa/bankalar listelenir.
      const code = isAccrualPayment ? lockedCurrencyCode : 'TRY';
      setSelectedCurrency({
        id: code,
        name: CURRENCY_NAMES[code] || code,
        symbol: getCurrencySymbol(code),
        code,
        isDefault: code === 'TRY'
      });
      setAmount(initialPaymentAmount || '');
      setDescription(initialPaymentDescription || '');
      setSubmitError(null);
      setInsufficientFunds(false);
      setInsufficientFundsMessage('');
      setSnackbarOpen(false);
      // ✅ Geçmiş tarihli işlem alanını sıfırla (varsayılan: kapalı / bugün)
      setIsBackdated(false);
      const today = new Date();
      setTransactionDate(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`);

      if (sourceAction === 'debt_receivable') {
        setSelectedPaymentMethod('accrual');
      } else if (sourceAction === 'payment_collection') {
        setSelectedPaymentMethod('cash');
      } else {
        setSelectedPaymentMethod('cash');
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, companyId, personnelId, sourceAction, initialPaymentDescription, initialPaymentAmount, accrualId, lockedCurrencyCode]);

  useEffect(() => {
    if (open && companyId && selectedCurrency) {
      loadCashboxesAndBanks();
    }
    if (!open) {
        setCashboxes([]);
        setBankAccounts([]);
        setFilteredCashboxes([]);
        setFilteredBankAccounts([]);
        setSelectedCashbox(null);
        setSelectedBankAccount(null);
    }
  }, [open, companyId, selectedCurrency]);

  useEffect(() => {
    if (selectedCurrency && cashboxes.length > 0) {
      const filtered = cashboxes.filter(cashbox => cashbox.currency === selectedCurrency.code);
      setFilteredCashboxes(filtered);
      if (filtered.length > 0) {
        const stillSelected = filtered.find(c => c.id === selectedCashbox?.id);
        if (stillSelected) setSelectedCashbox(stillSelected);
        else setSelectedCashbox(null); 
      } else {
        setSelectedCashbox(null);
      }
    } else {
      setFilteredCashboxes([]);
      setSelectedCashbox(null);
    }
  }, [selectedCurrency, cashboxes, selectedCashbox?.id]);

  useEffect(() => {
    if (selectedCurrency && bankAccounts.length > 0) {
      const filtered = bankAccounts.filter(account => account.currency === selectedCurrency.code);
      setFilteredBankAccounts(filtered);
      if (filtered.length > 0) {
        const stillSelected = filtered.find(b => b.id === selectedBankAccount?.id);
        if (stillSelected) setSelectedBankAccount(stillSelected);
        else setSelectedBankAccount(null);
      } else {
        setSelectedBankAccount(null);
      }
    } else {
      setFilteredBankAccounts([]);
      setSelectedBankAccount(null);
    }
  }, [selectedCurrency, bankAccounts, selectedBankAccount?.id]);

  const handleCloseInsufficientFundsModal = () => {
    setInsufficientFunds(false);
  };

  const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setSubmitError(null);
    setInsufficientFunds(false);
    const isAccrual = selectedPaymentMethod === 'accrual';
    const amountValue = parseFloat(amount);

    if (!isAccrual && selectedPaymentMethod === 'cash' && !selectedCashbox) {
      setSubmitError("Nakit ödeme için lütfen bir kasa seçin.");
      setSnackbarMessage("Nakit ödeme için lütfen bir kasa seçin.");
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      setLoading(false); 
      return;
    }
    if (!isAccrual && selectedPaymentMethod === 'bank' && !selectedBankAccount) {
      setSubmitError("Banka ödemesi için lütfen bir banka hesabı seçin.");
      setSnackbarMessage("Banka ödemesi için lütfen bir banka hesabı seçin.");
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      setLoading(false); 
      return;
    }
    if (!amount || amountValue <= 0) {
      setSubmitError("Lütfen geçerli bir tutar girin.");
      setSnackbarMessage("Lütfen geçerli bir tutar girin.");
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      setLoading(false);
      return;
    }
    if (isAccrualPayment && isAccrual) {
      setSubmitError("Hakediş ödemesi kasadan veya bankadan yapılmalıdır.");
      setLoading(false);
      return;
    }
    if (isAccrualPayment && accrualRemaining !== undefined && amountValue > accrualRemaining + 0.005) {
      const msg = `Ödeme kalan tutarı aşamaz (kalan: ${formatCurrency(accrualRemaining, lockedCurrencyCode)}).`;
      setSubmitError(msg);
      setSnackbarMessage(msg);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      setLoading(false);
      return;
    }

    // Nakit veya banka ödeme yöntemi seçilmişse bakiye kontrolü yap
    if (!isAccrual) {
      if (selectedPaymentMethod === 'cash' && selectedCashbox) {
        if (selectedCashbox.balance < amountValue) {
          const message = `Seçilen kasada yeterli bakiye bulunmamaktadır. Mevcut bakiye: ${formatCurrency(selectedCashbox.balance, selectedCashbox.currencyCode)}`;
          setInsufficientFundsMessage(message);
          setInsufficientFunds(true);
          setLoading(false);
          return;
        }
      } else if (selectedPaymentMethod === 'bank' && selectedBankAccount) {
        if (selectedBankAccount.balance < amountValue) {
          const message = `Seçilen banka hesabında yeterli bakiye bulunmamaktadır. Mevcut bakiye: ${formatCurrency(selectedBankAccount.balance, selectedBankAccount.currency)}`;
          setInsufficientFundsMessage(message);
          setInsufficientFunds(true);
          setLoading(false);
          return;
        }
      }
    }

    if (!companyId || !personnelId || !selectedCurrency) {
      setSubmitError("Gerekli alanlar eksik. Lütfen yöneticinize başvurun.");
      setSnackbarMessage("Gerekli alanlar eksik. Lütfen yöneticinize başvurun.");
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      setLoading(false);
      return;
    }

    // SQL id'leri hesapla (kasa/banka — alt hesap ise parent + sub ayrılır)
    const isCash = !isAccrual && selectedPaymentMethod === 'cash';
    const isBank = !isAccrual && selectedPaymentMethod === 'bank';
    let cashboxId: number | null = null;
    let bankAccountId: number | null = null;
    let bankSubAccountId: number | null = null;
    if (isCash && selectedCashbox) {
      cashboxId = parseInt(selectedCashbox.id, 10);
    }
    if (isBank && selectedBankAccount) {
      if (selectedBankAccount.isSubAccount && selectedBankAccount.parentBankAccountId) {
        bankAccountId = parseInt(selectedBankAccount.parentBankAccountId, 10);
        bankSubAccountId = parseInt(selectedBankAccount.id, 10);
      } else {
        bankAccountId = parseInt(selectedBankAccount.id, 10);
      }
    }

    // ✅ Geçmiş tarihli işlem: işaretliyse seçilen tarih ISO olarak gönderilir;
    // backend hareketin HEM Date HEM CreatedAt değerini bu tarihe yazar.
    let backdatedIso: string | undefined = undefined;
    if (isBackdated) {
      const selected = parseTransactionDate(transactionDate);
      if (!selected) {
        setSubmitError("Geçerli bir işlem tarihi seçiniz.");
        setSnackbarMessage("Geçerli bir işlem tarihi seçiniz.");
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        setLoading(false);
        return;
      }
      backdatedIso = selected.toISOString();
    }

    try {
      // Atomik SQL: personel hareketi + (varsa) kasa/banka hareketi + bakiye — tek POST
      await createPersonnelTransaction({
        personnelFirebaseUid: personnelId,
        personnelName,
        transactionType: sourceAction === 'payment_collection' ? 'PAYMENT' : 'DEBT',
        amount: amountValue,
        currency: selectedCurrency.code,
        description,
        paymentMethod: selectedPaymentMethod as 'accrual' | 'cash' | 'bank',
        cashboxId,
        bankAccountId,
        bankSubAccountId,
        transactionDate: backdatedIso,
        performedBy: getUserFullName(),
        kind: transactionKind,
        projectId,
        accrualId: isAccrualPayment ? parseInt(accrualId as string, 10) : null,
      });

      setSnackbarMessage(sourceAction === 'payment_collection' ? "Ödeme başarıyla kaydedildi!" : "Borç başarıyla kaydedildi!");
      setSnackbarSeverity('success');
      setSnackbarOpen(true);

      if (onSuccess) onSuccess();
      setTimeout(() => {
        onClose();
      }, 2000);

    } catch (err: any) {
      const msg = err?.response?.data?.message || err.message;
      console.error("Kayıt sırasında hata:", err);
      setSubmitError("Kayıt sırasında bir hata oluştu: " + msg);
      setSnackbarMessage("Kayıt sırasında bir hata oluştu: " + msg);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };
  
  const showSourceSelection = selectedPaymentMethod === 'cash' || selectedPaymentMethod === 'bank';

  return (
    <>
      <Dialog
        open={open}
        onClose={loading ? undefined : onClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {sourceAction === 'payment_collection' ? 'Personele Ödeme Yap' : 'Personele Borç Ekle'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <GuideButton guideKey="personnel.debt" color="inherit" disabled={loading} />
              <IconButton edge="end" color="inherit" onClick={onClose} disabled={loading}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        
        <DialogContent dividers>
          {submitError && (
            <MuiAlert severity="error" sx={{ mb: 2 }}>
              {submitError}
            </MuiAlert>
          )}
          {loading && cashboxes.length === 0 && bankAccounts.length === 0 && selectedPaymentMethod !== 'accrual' && (
              <Box sx={{display: 'flex', justifyContent: 'center', my:2}}><CircularProgress /></Box>
          )}
          
          <Box p={1}>
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Personel Bilgisi
              </Typography>
              <Typography variant="h6">{personnelName}</Typography>
              <Typography variant="body2" color="textSecondary">
                ID: {personnelId}
              </Typography>
            </Paper>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  {sourceAction === 'payment_collection' ? 'Ödeme Yöntemi' : 'İşlem Türü'}
                </Typography>
                <FormControl component="fieldset" fullWidth>
                  <RadioGroup
                    row
                    value={selectedPaymentMethod}
                    onChange={(e) => {
                        setSelectedPaymentMethod(e.target.value);
                        setSelectedCashbox(null);
                        setSelectedBankAccount(null);
                    }}
                  >
                    {availablePaymentMethods.map(method => (
                      <FormControlLabel
                        key={method.id}
                        value={method.id}
                        control={<Radio />}
                        label={method.name}
                        disabled={loading}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              </Grid>

              {showSourceSelection && selectedPaymentMethod === 'cash' && (
                  <Grid item xs={12}>
                      <Typography variant="subtitle2" gutterBottom>Kasa Seçimi</Typography>
                      <Select
                          value={selectedCashbox?.id || ''}
                          onChange={(e) => {
                              const selected = filteredCashboxes.find(c => c.id === e.target.value);
                              setSelectedCashbox(selected || null);
                          }}
                          displayEmpty
                          fullWidth
                          disabled={loading || filteredCashboxes.length === 0}
                          size="small"
                      >
                          <MenuItem value="" disabled>
                              {loading ? 'Yükleniyor...' : (filteredCashboxes.length === 0 ? 'Uygun kasa yok' : 'Kasa seçin')}
                          </MenuItem>
                          {filteredCashboxes.map((cashbox) => (
                          <MenuItem key={cashbox.id} value={cashbox.id}>
                              {cashbox.name} - {formatCurrency(cashbox.balance, cashbox.currencyCode)}
                          </MenuItem>
                          ))}
                      </Select>
                  </Grid>
              )}
               {showSourceSelection && selectedPaymentMethod === 'bank' && ( 
                  <Grid item xs={12}>
                       <Typography variant="subtitle2" gutterBottom>Banka Hesabı Seçimi</Typography>
                       <Select
                          value={selectedBankAccount?.id || ''}
                          onChange={(e) => {
                              const selected = filteredBankAccounts.find(b => b.id === e.target.value);
                              setSelectedBankAccount(selected || null);
                          }}
                          displayEmpty
                          fullWidth
                          disabled={loading || filteredBankAccounts.length === 0}
                          size="small"
                      >
                          <MenuItem value="" disabled>
                              {loading ? 'Yükleniyor...' : (filteredBankAccounts.length === 0 ? 'Uygun banka hesabı yok' : 'Banka seçin')}
                          </MenuItem>
                          {filteredBankAccounts.map((account) => (
                          <MenuItem key={account.id} value={account.id}>
                              {account.bankName} - {formatCurrency(account.balance, account.currency)}
                          </MenuItem>
                          ))}
                      </Select>
                  </Grid>
              )}

              {isAccrualPayment && (
                <Grid item xs={12}>
                  <MuiAlert severity="info">
                    Bu ödeme hakedişe bağlanır ({lockedCurrencyCode}). Kalan tutar:{' '}
                    <strong>{formatCurrency(accrualRemaining ?? 0, lockedCurrencyCode)}</strong>. Daha az öderseniz hakediş kısmi ödenmiş olarak açık kalır.
                  </MuiAlert>
                </Grid>
              )}

              <Grid item xs={12}>
                <TextField
                  label={sourceAction === 'payment_collection' ? 'Ödeme Tutarı' : 'Borç Tutarı'}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  fullWidth
                  type="number"
                  size="medium"
                  sx={{ my: 1 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {selectedCurrency?.symbol || '₺'}
                      </InputAdornment>
                    ),
                  }}
                  required
                  disabled={loading}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Açıklama"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  fullWidth
                  multiline
                  rows={3}
                  size="medium"
                  sx={{ mb: 1 }}
                  placeholder="İşlemle ilgili açıklama (opsiyonel)"
                  disabled={loading}
                />
              </Grid>

              {/* Proje bağı (PYS) */}
              {!hideProject && (
                <Grid item xs={12}>
                  <ProjectSelector value={projectId} onChange={setProjectId} disabled={loading} size="medium" />
                </Grid>
              )}

              {/* Geçmiş Tarihli İşlem */}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isBackdated}
                      onChange={(e) => setIsBackdated(e.target.checked)}
                      disabled={loading}
                      color="success"
                    />
                  }
                  label="Geçmiş tarihli işlem yap"
                />
                {isBackdated && (
                  <>
                    <AppDatePicker
                      label="İşlem Tarihi *"
                      value={transactionDate}
                      onChange={setTransactionDate}
                      fullWidth
                      required
                      disabled={loading}
                      disableFuture
                      size="medium"
                      sx={{ mt: 1, mb: 1 }}
                    />
                    <MuiAlert severity="warning" sx={{ mb: 1 }}>
                      Bu işlem seçtiğiniz tarihe kaydedilecektir. İşlem kaydı (ve oluşan kasa/banka hareketi) tamamen o tarihe ait olacak; bugüne dair bilgi içermeyecektir.
                    </MuiAlert>
                  </>
                )}
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
          <Button onClick={onClose} disabled={loading} variant="outlined">
            İptal
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={loading || !amount || parseFloat(amount) <= 0 
              || (showSourceSelection && selectedPaymentMethod === 'cash' && !selectedCashbox)
              || (showSourceSelection && selectedPaymentMethod === 'bank' && !selectedBankAccount)
              || (isBackdated && !transactionDate)
          }>
            {loading ? <CircularProgress size={24} color="inherit" /> : 
              (sourceAction === 'payment_collection' ? 'Ödemeyi Kaydet' : 'Borcu Kaydet')}
          </Button>
        </DialogActions>

        {showSourceSelection && (
          <Dialog open={insufficientFunds} onClose={handleCloseInsufficientFundsModal} maxWidth="xs" fullWidth >
            <DialogTitle>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6">Yetersiz Bakiye</Typography>
                <IconButton edge="end" color="inherit" onClick={handleCloseInsufficientFundsModal}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent>
              <MuiAlert severity="warning" sx={{ mb: 2 }}>
                {insufficientFundsMessage}
              </MuiAlert>
              <Typography variant="body2">
                Tutarı azaltın ya da başka bir kasa / banka hesabı seçin.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseInsufficientFundsModal} color="primary" variant="outlined">
                Tamam
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </Dialog>

      <Snackbar 
        open={snackbarOpen} 
        autoHideDuration={3000} 
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <MuiAlert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }} variant="filled">
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </>
  );
};

export default AddDebtModal; 